import { CheckCircleIcon } from '@heroicons/react/24/outline'
import { FormEvent, useCallback, useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation } from 'urql'
import { PRIVACY_URL } from '../../_constants/legal'
import { registerUserMutation } from '../../_lib/graphql/mutations'
import { FormField } from '../FormField'
import { PublicLayout } from '../PublicLayout'
import { useSnackbarPush } from '../SnackbarContext/SnackbarContext'


export const RegistrationPage = () => {
  const { pushError } = useSnackbarPush()
  const [registerState, executeRegister] = useMutation(registerUserMutation)
  const [isRegistered, setIsRegistered] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordRepeat, setPasswordRepeat] = useState('')
  const [firstName, setFirstName] = useState('')
  const [surname, setSurname] = useState('')
  const [birthday, setBirthday] = useState('')
  const [phone, setPhone] = useState('')
  const [acceptPrivacy, setAcceptPrivacy] = useState(false)

  const handleSubmit = useCallback((event: FormEvent) => {
    event.preventDefault()

    if (password !== passwordRepeat) {
      pushError('Die Passwörter stimmen nicht überein.')
      return
    }

    executeRegister({
      input: {
        email,
        password,
        firstName,
        surname,
        birthday,
        phone,
        acceptPrivacy
      }
    }).then(result => {
      if (result.error) {
        pushError(result.error.graphQLErrors[0]?.message || result.error.message)
        return
      }

      setIsRegistered(true)
    })
  }, [acceptPrivacy, birthday, email, executeRegister, firstName, password, passwordRepeat, phone, pushError, surname])

  return (
    <PublicLayout>
      <div className={'mx-auto mt-6 max-w-xl'}>
        {isRegistered ? (
          <div className={'rounded-3xl border border-sand-200 bg-white p-8 text-center shadow-sm'}>
            <CheckCircleIcon className={'mx-auto h-14 w-14 text-primary'} />
            <h1 className={'mt-4 text-3xl font-bold tracking-tight text-primary-200'}>
              Fast geschafft!
            </h1>
            <p className={'mt-3 text-base leading-7 text-gray-600'}>
              Wir haben dir eine E-Mail an {email} geschickt. Bestätige darin deine Adresse, danach kannst du dich anmelden und einem Festival beitreten.
            </p>
            <Link
              to={'/login'}
              className={'mt-6 inline-flex items-center justify-center rounded-full bg-primary-200 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'}
            >
              Zur Anmeldung
            </Link>
          </div>
        ) : (
          <div className={'rounded-3xl border border-sand-200 bg-white p-6 shadow-sm sm:p-8'}>
            <h1 className={'text-3xl font-bold tracking-tight text-primary-200 text-balance'}>
              Bei Crew-Backstage registrieren
            </h1>
            <p className={'mt-2 text-sm leading-6 text-gray-600'}>
              Mit einem Konto kannst du dich bei allen Festivals anmelden. Bereits registriert?{' '}
              <Link
                to={'/login'}
                className={'font-semibold text-primary hover:underline'}
              >
                Zur Anmeldung
              </Link>
            </p>

            <form
              onSubmit={handleSubmit}
              className={'mt-6 space-y-5'}
            >
              <div className={'grid gap-5 sm:grid-cols-2'}>
                <FormField
                  id={'firstName'}
                  label={'Vorname'}
                  autoComplete={'given-name'}
                  value={firstName}
                  onChange={setFirstName}
                />
                <FormField
                  id={'surname'}
                  label={'Nachname'}
                  autoComplete={'family-name'}
                  value={surname}
                  onChange={setSurname}
                />
              </div>
              <FormField
                id={'email'}
                label={'E-Mail-Adresse'}
                type={'email'}
                autoComplete={'email'}
                value={email}
                onChange={setEmail}
              />
              <div className={'grid gap-5 sm:grid-cols-2'}>
                <FormField
                  id={'birthday'}
                  label={'Geburtsdatum'}
                  type={'date'}
                  autoComplete={'bday'}
                  value={birthday}
                  onChange={setBirthday}
                />
                <FormField
                  id={'phone'}
                  label={'Telefonnummer'}
                  type={'tel'}
                  autoComplete={'tel'}
                  value={phone}
                  onChange={setPhone}
                />
              </div>
              <FormField
                id={'password'}
                label={'Passwort'}
                type={'password'}
                autoComplete={'new-password'}
                value={password}
                onChange={setPassword}
              />
              <FormField
                id={'passwordRepeat'}
                label={'Passwort bestätigen'}
                type={'password'}
                autoComplete={'new-password'}
                value={passwordRepeat}
                onChange={setPasswordRepeat}
              />

              <label className={'flex items-start gap-3 text-sm text-gray-700'}>
                <input
                  type={'checkbox'}
                  required
                  checked={acceptPrivacy}
                  onChange={event => setAcceptPrivacy(event.target.checked)}
                  className={'mt-0.5 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary'}
                />
                <span>
                  Ich habe die{' '}
                  <a
                    href={PRIVACY_URL}
                    target={'_blank'}
                    rel={'noreferrer'}
                    className={'font-semibold text-primary hover:underline'}
                  >
                    Datenschutzerklärung
                  </a>
                  {' '}gelesen und bin mit der Verarbeitung meiner Daten einverstanden.
                </span>
              </label>

              <button
                type={'submit'}
                disabled={registerState.fetching}
                className={'flex w-full justify-center rounded-full bg-primary-200 px-6 py-3 text-sm font-semibold text-white hover:bg-primary-100 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary'}
              >
                {registerState.fetching ? 'Wird gesendet …' : 'Konto erstellen'}
              </button>
            </form>
          </div>
        )}
      </div>
    </PublicLayout>
  )
}
